/**
 * ===================================================================
 * HEALTH INCIDENT FORM
 * ===================================================================
 * Formulář pro vytvoření a editaci zdravotního incidentu.
 * Integruje katalog škůdců/nemocí z Plant-O-Pedie (P3.2).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import logger from '../../utils/logger';
import {
  X,
  AlertTriangle,
  Bug,
  Leaf,
  ThermometerSun,
  Wrench,
  Search,
  Plus,
  ChevronDown,
  Beaker,
  Hand,
  BookOpen,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Modal } from '../UI/Modal';
import { Button, Input, RadioTile, Select, Textarea } from '../UI';
import { useCreateHealthIncident, useUpdateHealthIncident } from '../../hooks/useHealthIncidents';
import { PestDiseaseService } from '../../services/pest-disease.service';
import type {
  CreateHealthIncidentDTO,
  UpdateHealthIncidentDTO,
  HealthIncidentType,
  HealthIncident,
} from '../../types/health-incidents';
import type { Pest, Disease } from '../../types/pest-disease';
import type { ControlInfo } from '../../types/common';
import { INCIDENT_TYPE_LABELS, SEVERITY_LABELS } from '../../types/health-incidents';

interface HealthIncidentFormProps {
  areaId: string;
  slotId?: string;
  plantId?: string;
  incident?: HealthIncident;
  onClose: () => void;
  onSuccess?: () => void;
}

type FormData = {
  incident_type: HealthIncidentType;
  name: string;
  species_pest_id?: string;
  severity: number;
  symptoms: string;
  notes: string;
  observed_at: string;
};

// Inline creation form data
type NewEntryData = {
  name_ui: string;
  category: string;
  visual_signs: string;
  control_biological: string;
  control_chemical: string;
  control_mechanical: string;
  default_severity: number;
};

type CatalogEntry = (Pest | Disease) & { _type: 'pest' | 'disease' };

const TYPE_OPTIONS: { value: HealthIncidentType; label: string; icon: React.ReactNode }[] = [
  { value: 'pest', label: INCIDENT_TYPE_LABELS.pest, icon: <Bug size={18} /> },
  { value: 'disease', label: INCIDENT_TYPE_LABELS.disease, icon: <AlertTriangle size={18} /> },
  {
    value: 'nutrient_deficiency',
    label: INCIDENT_TYPE_LABELS.nutrient_deficiency,
    icon: <Leaf size={18} />,
  },
  {
    value: 'environmental',
    label: INCIDENT_TYPE_LABELS.environmental,
    icon: <ThermometerSun size={18} />,
  },
  { value: 'mechanical', label: INCIDENT_TYPE_LABELS.mechanical, icon: <Wrench size={18} /> },
  { value: 'other', label: INCIDENT_TYPE_LABELS.other, icon: <AlertTriangle size={18} /> },
];

const PEST_CATEGORIES = ['insect', 'mite', 'larva', 'slug', 'rodent', 'other'];
const DISEASE_CATEGORIES = ['fungus', 'bacteria', 'virus', 'oomycete', 'physiological', 'other'];

// ─── Treatment Recommendations Panel ────────────────────────────────────────
const TreatmentPanel: React.FC<{ control?: ControlInfo }> = ({ control }) => {
  if (!control) return null;

  const hasContent =
    control.biological?.length || control.chemical?.length || control.mechanical?.length;
  if (!hasContent) return null;

  const sections = [
    {
      key: 'biological',
      label: 'Biologická kontrola',
      icon: <Leaf size={16} className="text-green-500" />,
      items: control.biological,
      color: 'green',
    },
    {
      key: 'chemical',
      label: 'Chemická kontrola',
      icon: <Beaker size={16} className="text-amber-500" />,
      items: control.chemical,
      color: 'amber',
    },
    {
      key: 'mechanical',
      label: 'Mechanická kontrola',
      icon: <Hand size={16} className="text-cyan-500" />,
      items: control.mechanical,
      color: 'cyan',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={16} className="text-emerald-600" />
        <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
          Doporučená řešení z Plant-O-Pedie
        </span>
      </div>
      <div className="space-y-3">
        {sections.map((s) =>
          s.items?.length ? (
            <div key={s.key}>
              <div className="flex items-center gap-1.5 mb-1">
                {s.icon}
                <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">
                  {s.label}
                </span>
              </div>
              <ul className="ml-6 space-y-0.5">
                {s.items.map((item, i) => (
                  <li key={i} className="text-xs text-gray-600 dark:text-zinc-400 list-disc">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null,
        )}
        {control.notes?.length ? (
          <div className="pt-2 border-t border-emerald-200 dark:border-emerald-800">
            <span className="text-xs text-gray-500 dark:text-zinc-400 italic">
              {control.notes.join(' ')}
            </span>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
};

// ─── Main Form ──────────────────────────────────────────────────────────────
export const HealthIncidentForm: React.FC<HealthIncidentFormProps> = ({
  areaId,
  slotId,
  plantId,
  incident,
  onClose,
  onSuccess,
}) => {
  const isEditing = !!incident;

  const createMutation = useCreateHealthIncident();
  const updateMutation = useUpdateHealthIncident();

  // Catalog state
  const [catalogItems, setCatalogItems] = useState<CatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCatalogEntry, setSelectedCatalogEntry] = useState<CatalogEntry | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [creatingEntry, setCreatingEntry] = useState(false);

  // New entry inline form
  const [newEntry, setNewEntry] = useState<NewEntryData>({
    name_ui: '',
    category: '',
    visual_signs: '',
    control_biological: '',
    control_chemical: '',
    control_mechanical: '',
    default_severity: 3,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      incident_type: incident?.incident_type || 'pest',
      name: incident?.name || '',
      species_pest_id: incident?.species_pest_id,
      severity: incident?.severity || 3,
      symptoms: incident?.symptoms || '',
      notes: incident?.notes || '',
      observed_at: incident?.observed_at
        ? new Date(incident.observed_at).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
    },
  });

  const selectedType = watch('incident_type');
  const isCatalogType = selectedType === 'pest' || selectedType === 'disease';

  // Load catalog when type changes to pest/disease
  useEffect(() => {
    if (!isCatalogType) {
      setCatalogItems([]);
      setSelectedCatalogEntry(null);
      return;
    }

    const loadCatalog = async () => {
      setCatalogLoading(true);
      try {
        if (selectedType === 'pest') {
          const result = await PestDiseaseService.getPests();
          setCatalogItems(result.data.map((p: Pest) => ({ ...p, _type: 'pest' as const })));
        } else {
          const result = await PestDiseaseService.getDiseases();
          setCatalogItems(result.data.map((d: Disease) => ({ ...d, _type: 'disease' as const })));
        }
      } catch (err) {
        logger.error('Failed to load catalog', { error: err as Error });
      } finally {
        setCatalogLoading(false);
      }
    };

    loadCatalog();
  }, [selectedType, isCatalogType]);

  // Filter catalog items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return catalogItems;
    const q = searchQuery.toLowerCase();
    return catalogItems.filter(
      (item) =>
        item.name_ui.toLowerCase().includes(q) ||
        item.latin_name?.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q),
    );
  }, [catalogItems, searchQuery]);

  // Handle selecting a catalog entry
  const handleSelectCatalogEntry = (entry: CatalogEntry) => {
    setSelectedCatalogEntry(entry);
    setValue('name', entry.name_ui);
    setValue('species_pest_id', entry.id);
    if (entry.default_severity) {
      setValue('severity', entry.default_severity);
    }
    setShowDropdown(false);
    setSearchQuery('');
    setShowCreateNew(false);
  };

  // Handle creating a new entry
  const handleCreateNewEntry = async () => {
    if (!newEntry.name_ui.trim()) return;

    setCreatingEntry(true);
    try {
      const controlInfo: ControlInfo = {};
      if (newEntry.control_biological.trim()) {
        controlInfo.biological = newEntry.control_biological
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (newEntry.control_chemical.trim()) {
        controlInfo.chemical = newEntry.control_chemical
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (newEntry.control_mechanical.trim()) {
        controlInfo.mechanical = newEntry.control_mechanical
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      const identInfo = newEntry.visual_signs.trim()
        ? {
            visual_signs: newEntry.visual_signs
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          }
        : undefined;

      const payload = {
        name_ui: newEntry.name_ui.trim(),
        category: newEntry.category || undefined,
        identification: identInfo,
        control: Object.keys(controlInfo).length ? controlInfo : undefined,
        default_severity: newEntry.default_severity,
      };

      let createdEntry: CatalogEntry;
      if (selectedType === 'pest') {
        const result = await PestDiseaseService.createPest(payload);
        createdEntry = { ...result.data, _type: 'pest' as const };
      } else {
        const result = await PestDiseaseService.createDisease(payload);
        createdEntry = { ...result.data, _type: 'disease' as const };
      }

      // Refresh catalog and select the new entry
      setCatalogItems((prev) => [...prev, createdEntry]);
      handleSelectCatalogEntry(createdEntry);
      setShowCreateNew(false);
    } catch (err) {
      logger.error('Failed to create catalog entry', { error: err as Error });
    } finally {
      setCreatingEntry(false);
    }
  };

  // Clear catalog selection
  const handleClearSelection = () => {
    setSelectedCatalogEntry(null);
    setValue('name', '');
    setValue('species_pest_id', undefined);
  };

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && incident) {
        const updateData: UpdateHealthIncidentDTO = {
          incident_type: data.incident_type,
          name: data.name,
          severity: data.severity as 1 | 2 | 3 | 4 | 5,
          symptoms: data.symptoms || undefined,
          notes: data.notes || undefined,
        };
        await updateMutation.mutateAsync({ id: incident.id, data: updateData });
      } else {
        const createData: CreateHealthIncidentDTO = {
          area_id: areaId,
          slot_id: slotId,
          plant_id: plantId,
          incident_type: data.incident_type,
          name: data.name,
          species_pest_id: data.species_pest_id,
          severity: data.severity as 1 | 2 | 3 | 4 | 5,
          symptoms: data.symptoms || undefined,
          notes: data.notes || undefined,
          observed_at: data.observed_at,
        };
        await createMutation.mutateAsync(createData);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      logger.error('Chyba pri ukladani incidentu', { error: error as Error });
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" className="max-w-lg">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-h-[90vh] overflow-y-auto p-6"
      >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Upravit incident' : 'Nahlásit problém'}
            </h2>
            <Button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              variant="ghost"
              className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800"
            >
              <X size={20} className="text-gray-500" />
            </Button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Typ problému */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                Typ problému *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_OPTIONS.map((option) => (
                  <RadioTile
                    key={option.value}
                    value={option.value}
                    checked={selectedType === option.value}
                    icon={option.icon}
                    label={option.label}
                    {...register('incident_type', { required: 'Vyberte typ' })}
                  />
                ))}
              </div>
              {errors.incident_type && (
                <p className="mt-1 text-sm text-red-500">{errors.incident_type.message}</p>
              )}
            </div>

            {/* ─── Catalog Dropdown (pest/disease only) ─── */}
            <AnimatePresence>
              {isCatalogType && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                    Vybrat z katalogu Plant-O-Pedie
                  </label>

                  {selectedCatalogEntry ? (
                    // Selected entry display
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg">
                      <div>
                        <span className="font-medium text-green-800 dark:text-green-300">
                          {selectedCatalogEntry.name_ui}
                        </span>
                        {selectedCatalogEntry.latin_name && (
                          <span className="ml-2 text-xs italic text-green-600 dark:text-green-500">
                            ({selectedCatalogEntry.latin_name})
                          </span>
                        )}
                        {selectedCatalogEntry.category && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-500 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                            {selectedCatalogEntry.category}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={handleClearSelection}
                        aria-label="Zrušit výběr"
                        variant="ghost"
                        className="p-1 hover:bg-green-200 dark:hover:bg-green-800 rounded"
                      >
                        <X size={16} className="text-green-700 dark:text-green-400" />
                      </Button>
                    </div>
                  ) : (
                    // Search/dropdown
                    <div className="relative">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder={
                          catalogLoading
                            ? 'Načítám katalog...'
                            : `Hledat ${selectedType === 'pest' ? 'škůdce' : 'nemoc'}...`
                        }
                        aria-label={`Hledat ${selectedType === 'pest' ? 'škůdce' : 'nemoc'} v katalogu`}
                        leftIcon={<Search size={16} className="text-gray-400" />}
                        rightIcon={<ChevronDown size={16} className="text-gray-400" />}
                        className="bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg text-sm"
                      />

                      {/* Dropdown */}
                      <AnimatePresence>
                        {showDropdown && !catalogLoading && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            className="absolute z-10 mt-1 w-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                          >
                            {filteredItems.length > 0 ? (
                              filteredItems.map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => handleSelectCatalogEntry(item)}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors flex items-center justify-between"
                                >
                                  <div>
                                    <span className="text-sm font-medium text-gray-800 dark:text-zinc-200">
                                      {item.name_ui}
                                    </span>
                                    {item.latin_name && (
                                      <span className="ml-1.5 text-xs italic text-gray-500">
                                        {item.latin_name}
                                      </span>
                                    )}
                                  </div>
                                  {item.category && (
                                    <span className="text-xs text-gray-400 bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">
                                      {item.category}
                                    </span>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                Žádný záznam nenalezen
                              </div>
                            )}

                            {/* Create new button */}
                            <Button
                              type="button"
                              onClick={() => {
                                setShowDropdown(false);
                                setShowCreateNew(true);
                                setNewEntry((prev) => ({ ...prev, name_ui: searchQuery }));
                              }}
                              variant="ghost"
                              fullWidth
                              className="justify-start rounded-none border-t border-gray-200 dark:border-zinc-700 hover:bg-green-50 dark:hover:bg-green-900/20 text-green-700 dark:text-green-400"
                              leftIcon={<Plus size={16} />}
                            >
                              <span className="text-sm font-medium">
                                Vytvořit nový{' '}
                                {selectedType === 'pest' ? 'záznam škůdce' : 'záznam nemoci'}
                              </span>
                            </Button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* ─── Inline Create New Entry ─── */}
                  <AnimatePresence>
                    {showCreateNew && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 p-4 bg-cyan-50/50 dark:bg-cyan-900/10 border border-cyan-200 dark:border-cyan-800 rounded-xl space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">
                            Nový {selectedType === 'pest' ? 'škůdce' : 'nemoc'} do katalogu
                          </h4>
                          <Button
                            type="button"
                            onClick={() => setShowCreateNew(false)}
                            variant="ghost"
                            className="p-1 hover:bg-cyan-200 dark:hover:bg-cyan-800 rounded"
                            aria-label="Zavřít formulář"
                          >
                            <X size={14} className="text-cyan-600" />
                          </Button>
                        </div>

                        {/* Name */}
                        <Input
                          type="text"
                          value={newEntry.name_ui}
                          onChange={(e) =>
                            setNewEntry((prev) => ({ ...prev, name_ui: e.target.value }))
                          }
                          placeholder="Název *"
                          className="text-sm bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />

                        {/* Category */}
                        <Select
                          value={newEntry.category}
                          onChange={(e) =>
                            setNewEntry((prev) => ({ ...prev, category: e.target.value }))
                          }
                          title="Kategorie katalogového záznamu"
                          options={[
                            { value: '', label: '-- Kategorie --' },
                            ...(selectedType === 'pest' ? PEST_CATEGORIES : DISEASE_CATEGORIES).map((cat) => ({
                              value: cat,
                              label: cat,
                            })),
                          ]}
                          className="text-sm bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />

                        {/* Visual signs */}
                        <Input
                          type="text"
                          value={newEntry.visual_signs}
                          onChange={(e) =>
                            setNewEntry((prev) => ({ ...prev, visual_signs: e.target.value }))
                          }
                          placeholder="Vizuální znaky (oddělte čárkou)"
                          className="text-sm bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-400"
                        />

                        {/* Control methods */}
                        <div className="space-y-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">
                            Doporučené kontrolní metody:
                          </span>
                          <Input
                            type="text"
                            value={newEntry.control_biological}
                            onChange={(e) =>
                              setNewEntry((prev) => ({
                                ...prev,
                                control_biological: e.target.value,
                              }))
                            }
                            placeholder="🌿 Biologická (oddělte čárkou)"
                            className="text-xs bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-400"
                          />
                          <Input
                            type="text"
                            value={newEntry.control_chemical}
                            onChange={(e) =>
                              setNewEntry((prev) => ({ ...prev, control_chemical: e.target.value }))
                            }
                            placeholder="🧪 Chemická (oddělte čárkou)"
                            className="text-xs bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-amber-400"
                          />
                          <Input
                            type="text"
                            value={newEntry.control_mechanical}
                            onChange={(e) =>
                              setNewEntry((prev) => ({
                                ...prev,
                                control_mechanical: e.target.value,
                              }))
                            }
                            placeholder="✋ Mechanická (oddělte čárkou)"
                            className="text-xs bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-400"
                          />
                        </div>

                        {/* Create button */}
                        <Button
                          type="button"
                          onClick={handleCreateNewEntry}
                          loading={creatingEntry}
                          disabled={!newEntry.name_ui.trim()}
                          variant="gradient"
                          fullWidth
                          className="text-sm"
                        >
                          Uložit do katalogu a použít
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Treatment Recommendations */}
                  {selectedCatalogEntry?.control && (
                    <div className="mt-3">
                      <TreatmentPanel control={selectedCatalogEntry.control} />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Název (manual when no catalog, or hidden when catalog selected) */}
            {(!isCatalogType || !selectedCatalogEntry) && !showCreateNew && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                  Název problému *
                </label>
                <Input
                  {...register('name', { required: 'Zadejte název' })}
                  type="text"
                  placeholder="např. Mšice, Padlí, Nedostatek dusíku..."
                  className="bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
              </div>
            )}

            {/* Závažnost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                Závažnost *
              </label>
              <Controller
                name="severity"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((level) => {
                      const colors = ['green', 'cyan', 'yellow', 'orange', 'red'];
                      const isSelected = field.value === level;
                      return (
                        <button
                          key={level}
                          type="button"
                          onClick={() => field.onChange(level)}
                          className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                            isSelected
                              ? `border-${colors[level - 1]}-500 bg-${colors[level - 1]}-50 text-${colors[level - 1]}-700`
                              : 'border-gray-200 dark:border-zinc-700 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {SEVERITY_LABELS[level]}
                        </button>
                      );
                    })}
                  </div>
                )}
              />
            </div>

            {/* Symptomy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                Symptomy
              </label>
              <Textarea
                {...register('symptoms')}
                rows={3}
                placeholder="Popište pozorované symptomy..."
                className="bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Poznámky */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                Poznámky
              </label>
              <Textarea
                {...register('notes')}
                rows={2}
                placeholder="Další poznámky..."
                className="bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Datum pozorování */}
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                  Datum pozorování
                </label>
                <Input
                  {...register('observed_at')}
                  type="datetime-local"
                  className="bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="secondary"
                fullWidth
                className="text-gray-700 dark:text-zinc-300 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700"
              >
                Zrušit
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                variant="danger"
                fullWidth
              >
                {isEditing ? 'Uložit změny' : 'Nahlásit'}
              </Button>
            </div>
          </form>
      </motion.div>
    </Modal>
  );
};

export default HealthIncidentForm;
